import { ExternalLinkIcon } from "@chakra-ui/icons";
import { useIntl } from "react-intl";

import { RanaButton } from "@nens/ranalib";
import { ThreediModel } from "@nens/threedi/openapi-client";

import { useCanSeeModelSchematisation } from "@/hooks/use-can-see-model-schematisation";
import { useHccLiveSiteAccessQuery } from "@/queries/query-hcc-live-site-access";
import { useThreediModelQuery } from "@/queries/query-hcc-models";
import { useHccSchematisationQuery } from "@/queries/query-hcc-schematisation";
import { useHccLatestRevisionQuery } from "@/queries/query-hcc-schematisation-revision";
import { useHccSimulationTemplatesQuery } from "@/queries/query-hcc-simulation-templates";

const LIVE_SITE_QUICKSTART = "/live/quickstart/simulation-template";

export function SchematisationLiveSiteAction({
  schematisationId,
  model,
}: {
  schematisationId: number;
  model?: ThreediModel | null;
}) {
  const intl = useIntl();
  const canSeeModelSchematisation = useCanSeeModelSchematisation();

  const liveSiteAccessQuery = useHccLiveSiteAccessQuery();
  const { data: schematisation } = useHccSchematisationQuery(schematisationId);
  const { data: latestRevision } = useHccLatestRevisionQuery(model ? undefined : schematisationId);
  const { data: queriedModel } = useThreediModelQuery({
    schematisationId,
    revisionId: model ? undefined : latestRevision?.id,
  });
  const { data: templatesResponse } = useHccSimulationTemplatesQuery(model?.id ?? queriedModel?.id);
  const template = templatesResponse?.results?.[0];
  const modelToUse = model ?? queriedModel;

  if (!canSeeModelSchematisation) {
    return null;
  }

  let href = undefined;
  const disabledTitle = (() => {
    if (!liveSiteAccessQuery.hasAccess) {
      return intl.formatMessage({
        id: "file.schematisation.template_run_on_live_disabled_no_access",
        defaultMessage: "You do not have access to Rana Live",
      });
    }
    if (!modelToUse?.id) {
      return intl.formatMessage({
        id: "file.schematisation.template_run_on_live_disabled_no_model",
        defaultMessage: "No model is available for this schematisation",
      });
    }
    if (!template?.id) {
      return intl.formatMessage({
        id: "file.schematisation.template_run_on_live_disabled_no_template",
        defaultMessage: "No simulation template is available for this model",
      });
    }
    if (!schematisation?.owner) {
      return intl.formatMessage({
        id: "file.schematisation.template_run_on_live_disabled_no_owner",
        defaultMessage: "No owner is available for this schematisation",
      });
    }
    href = `${window.location.origin}${LIVE_SITE_QUICKSTART}/${modelToUse.id}/${template.id}/${schematisation.owner}/`;
    return undefined;
  })();

  return (
    <RanaButton
      as="a"
      variant="secondary"
      leftIcon={<ExternalLinkIcon />}
      isDisabled={!!disabledTitle}
      title={disabledTitle}
      href={href}
    >
      {intl.formatMessage({
        id: "file.schematisation.template_run_on_live",
        defaultMessage: "Run in Rana Live",
      })}
    </RanaButton>
  );
}
